import React, {useContext, useEffect, useState} from "react";
import Modal from "@amzn/meridian/modal";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import Text from "@amzn/meridian/text";
import {FormattedMessage, injectIntl} from "react-intl";
import PageFooterModal from "../../../commonui/PageFooterModal";
import Input from "@amzn/meridian/input";
import Alert from "@amzn/meridian/alert";
import {
    getMaxPortsForLedControllerType,
    hasLedPortAboveLimit,
    isValidControllerIp,
    isValidEntry,
    MAC_MASK,
    normalizeIpAddress,
    PATTERNS,
    validateNewLedController
} from "../../../util/Validations";
import {FEATURES, FeatureUtility} from "../../../util/FeatureUtility";
import Column from "@amzn/meridian/column";
import {LEDControllerOverviewContext} from "../context/LEDControllerOverviewContext";
import {DEFAULT_AISLE_TYPE} from "../../../util/Constants";
import {createNextAisleConfigs} from "../../../util/LEDControllerUtil";
import AisleRangeInputModal from "./AisleRangeInputModal";

const AddSingleLedControllerModal = (props) => {

    const {formatMessage} = props.intl;
    const {open, setOpen} = props;
    const {showWarning} = useContext(GlobalStateContext);
    const {viewModel, dispatch} = useContext(LEDControllerOverviewContext);
    const maxPorts = getMaxPortsForLedControllerType(
        viewModel.selectedDefaultValues && viewModel.selectedDefaultValues.ledControllerType);

    // Visual controls
    const [showAlert, setShowAlert] = useState(false)
    const [showPortLimitAlert, setShowPortLimitAlert] = useState(false)

    const isIpCaptureEnabled = FeatureUtility.getFeatureStatus(FEATURES.IS_LED_CONTROLLER_IP_CAPTURE_ENABLED)

    // States
    const [mac, setMac] = useState(null)
    const [ip, setIp] = useState(null)
    const [startAisle, setStartAisle] = useState(null)
    const [startPort, setStartPort] = useState(null);
    const [aisleCount, setAisleCount] = useState(null)
    const [aisleType, setAisleType] = useState(null)

    // Errors
    const [macError, setMacError] = useState(false)
    const [ipError, setIpError] = useState(false)
    const [startAisleError, setStartAisleError] = useState(false)
    const [startPortError, setStartPortError] = useState(false)
    const [aisleCountError, setAisleCountError] = useState(false)
    const [aisleTypeError, setAisleTypeError] = useState(false)
    const {showCustomAlert} = useContext(GlobalStateContext);

    useEffect(() => {
        getAisleTypes();
    }, []);

    const stateValues = {
        ledControllerConfig: {
            macAddress: mac,
            ...(isIpCaptureEnabled && {ipAddress: ip && ip.trim()}),
            startAisleLabel: startAisle,
            startPortNumber: startPort,
            aisleCount: aisleCount,
            aisleType: aisleType
        }
    }

    const resetAssetDetails = () => {
        setMac(null)
        setIp(null)
        setStartAisle(null)
        setStartPort(null)
        setAisleCount(null)
        setAisleType(null)
    }

    const resetAssetDetailErrors = () => {
        setMacError(false)
        setIpError(false)
        setStartAisleError(false)
        setStartPortError(false)
        setAisleCountError(false)
    }

    const resetInputState = () => {
        resetAssetDetails()
        resetAssetDetailErrors()
    }

    const onCloseModal = () => {
        setOpen(false)
        resetInputState()
        setShowAlert(false);
        setShowPortLimitAlert(false);
    }

    const addData = (stateValues) => {
        const aisleDetails = createNextAisleConfigs(stateValues.ledControllerConfig, 0);
        const model = {
            aisleDetails: aisleDetails,
            macAddress: stateValues.ledControllerConfig.macAddress,
            ...(isIpCaptureEnabled
                && {ipAddress: normalizeIpAddress(stateValues.ledControllerConfig.ipAddress)})
        }
        dispatch({type: 'ADD_NEW_DEVICE', data: {dispatcher: dispatch, model: model,
                showCustomAlert: showCustomAlert}})
    }

    const getAisleTypes = () => {
        if (viewModel.aisleTypes.size === 0) {
            dispatch({type: 'LOAD_AISLE_TYPES', data: {dispatcher: dispatch}})
        }
    }

    const onClickConfirm = (stateValues) => {
        const {startPortNumber, aisleCount} = stateValues.ledControllerConfig;
        if (validateNewLedController(stateValues, maxPorts)) {
            showWarning(formatMessage({id: "device.editWarning"}), () => {
                setShowAlert(false)
                setShowPortLimitAlert(false)
                addData(stateValues);
                resetInputState();
                setOpen(false);
            });
        } else if (hasLedPortAboveLimit([parseInt(startPortNumber, 10) + parseInt(aisleCount, 10) - 1], maxPorts)) {
            setShowAlert(false)
            setShowPortLimitAlert(true)
        } else {
            setShowAlert(true)
            setShowPortLimitAlert(false)
        }
    }


    return (
        <Modal
            title={formatMessage({id: "device.add_only_device_button_modal"})}
            open={open}
            onOpen={resetInputState}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="100%">
            <Column spacing={"medium"} spacingInset={"medium"}>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="mac-input">
                        <FormattedMessage id="modal.mac" defaultMessage="MAC Address"/>
                    </Text>
                    <Input id="mac-input"
                           value={mac}
                           onChange={setMac}
                           mask={MAC_MASK}
                           onBlur={() => {
                               setMacError(!isValidEntry(stateValues.ledControllerConfig.macAddress, PATTERNS.MAC))
                           }}
                           error={macError}
                    />
                </Column>
                {isIpCaptureEnabled &&
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="ip-input">
                            <FormattedMessage id="modal.ip" defaultMessage="IP Address"/>
                        </Text>
                        <Input id="ip-input"
                               value={ip}
                               onChange={setIp}
                               onBlur={() => setIpError(!isValidControllerIp(stateValues.ledControllerConfig.ipAddress))}
                               error={ipError}
                        />
                    </Column>
                }
                <AisleRangeInputModal startAisle={startAisle} setStartAisle={setStartAisle}
                                      startAisleError={startAisleError} setStartAisleError={setStartAisleError}
                                      startPort={startPort} setStartPort={setStartPort}
                                      startPortError={startPortError} setStartPortError={setStartPortError}
                                      aisleCount={aisleCount} setAisleCount={setAisleCount}
                                      aisleCountError={aisleCountError} setAisleCountError={setAisleCountError}
                                      aisleType={aisleType} setAisleType={setAisleType}
                                      aisleTypeError={aisleTypeError} setAisleTypeError={setAisleTypeError}
                                      getAisleTypeOptions={[...viewModel.aisleTypes, DEFAULT_AISLE_TYPE] || []}
                                      stateValues={stateValues}
                                      maxPorts={maxPorts}
                />
                {showPortLimitAlert && <Alert type="warning" size="small">
                    <FormattedMessage id="alert.portoutofrange"
                                      defaultMessage="Port numbers must be between 1 and {maxPorts} for this LED controller type!"
                                      values={{maxPorts}}/>
                </Alert>}
            </Column>
            {<PageFooterModal showAlert={showAlert} onCloseModalCallBack={onCloseModal}
                          onClickConfirmCallBack={() => onClickConfirm(stateValues)}
            />}
        </Modal>
    )

}

export default injectIntl(AddSingleLedControllerModal);


// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/modal/AddSingleLedControllerModal.js